import { apiClient } from '../lib/apiClient';

/**
 * Admin API used by the admin area (/admin/*). All endpoints require the
 * Cognito 'admin' group (backend RBAC); see src/routes/mounts/adminRoutes.ts.
 */

// ─── Users: /api/v1/admin/users ──────────────────────────────────────────────

export interface AdminUserLicense {
  id: string;
  plan: string;
  status: 'active' | 'expired' | 'revoked';
  expires_at: string | null;
}

/** A user as GET /api/v1/admin/users returns it (snake_case, see admin-users.controller.ts). */
export interface AdminUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  /** 'active' | 'inactive' | 'deactivated' | 'anonymized' */
  status: string;
  mfa_enabled?: boolean;
  created_at: string;
  /** Last successful sign-in (ISO); null = never signed in (missing on older APIs). */
  last_login_at?: string | null;
  license?: AdminUserLicense | null;
  /** Closed beta: 'pending' | 'approved' | 'rejected' (older APIs: missing = approved). */
  approval_status?: AdminApprovalStatus;
  approved_at?: string | null;
  rejected_at?: string | null;
  /** E-mail choices made at sign-up or later. */
  email_consents?: {
    product_updates: boolean;
    product_updates_confirmed: boolean;
    task_reminders: boolean;
  };
}

export type AdminApprovalStatus = 'pending' | 'approved' | 'rejected';

/** 'pending' = the approval queue; 'rejected' = accounts that were not approved. */
export type AdminUserStatusFilter = 'all' | 'deactivated' | 'pending' | 'rejected';

export function approvalStatusOf(user: AdminUser): AdminApprovalStatus {
  return user.approval_status ?? 'approved';
}

export async function fetchAdminUsers(filter: AdminUserStatusFilter = 'all'): Promise<{ users: AdminUser[]; total: number }> {
  const params = new URLSearchParams({ limit: '100' });
  if (filter === 'deactivated') params.set('status', 'deactivated');
  if (filter === 'pending' || filter === 'rejected') params.set('approval', filter);
  const res = await apiClient.get<{ data: { users: AdminUser[]; total: number } }>(`/api/v1/admin/users?${params.toString()}`);
  return res.data;
}

/** Closed beta: approve a pending or rejected account (idempotent). */
export async function approveUser(userId: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/approve`);
}

/** Closed beta: reject a pending account (idempotent). */
export async function rejectUser(userId: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/users/${encodeURIComponent(userId)}/reject`);
}

const userPath = (id: string): string => `/api/v1/admin/users/${encodeURIComponent(id)}`;

/** Grants or withdraws the admin role: POST / DELETE /api/v1/admin/users/:id/role. */
export async function setAdminRole(userId: string, makeAdmin: boolean): Promise<void> {
  if (makeAdmin) await apiClient.post(`${userPath(userId)}/role`, { role: 'admin' });
  else await apiClient.delete(`${userPath(userId)}/role`, { body: { role: 'admin' } });
}

export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.post(`${userPath(userId)}/deactivate`);
}

export async function reactivateUser(userId: string): Promise<void> {
  await apiClient.post(`${userPath(userId)}/reactivate`);
}

/** MFA reset: switches the user's e-mail MFA off (they can set it up again). */
export async function resetUserMfa(userId: string): Promise<void> {
  await apiClient.post(`${userPath(userId)}/mfa/disable`);
}

/**
 * Account erasure (DELETE /api/v1/admin/users/:id?mode=hard-delete): signs the
 * user out everywhere, deletes the Cognito user and all their data
 * (services/accountErasure.service.ts).
 */
export async function deleteUserAccount(userId: string): Promise<void> {
  await apiClient.delete(`${userPath(userId)}?mode=hard-delete`);
}

// ─── Licences: /api/v1/admin/licenses ────────────────────────────────────────

export interface AdminLicense {
  id: string;
  userId: string;
  userEmail: string | null;
  plan: string;
  isActive: boolean;
  /** Admin listing status: is_active = false → 'inactive' (revoked). */
  status: 'active' | 'inactive' | 'expired';
  activatedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface LicenseSummary {
  totalLicenses: number;
  usedLicenses: number;
  availableLicenses: number;
}

export type IssuablePlan = 'standard' | 'reef' | 'reef_pro';

export interface CreateLicenseInput {
  email: string;
  plan: IssuablePlan;
  /** undefined = unlimited. */
  validDays?: number;
  /** true: apply to the account now; false: only issue a key. */
  apply: boolean;
}

export interface CreatedLicense {
  id: string | null;
  userEmail: string;
  licenseCode: string;
  plan: string;
  applied: boolean;
  validDays: number | null;
  expiresAt: string | null;
}

export async function fetchAdminLicenses(): Promise<AdminLicense[]> {
  const res = await apiClient.get<{ data: AdminLicense[] }>('/api/v1/admin/licenses');
  return res.data;
}

export async function fetchLicenseSummary(): Promise<LicenseSummary> {
  const res = await apiClient.get<{ data: LicenseSummary }>('/api/v1/admin/licenses/summary');
  return res.data;
}

export async function createLicense(input: CreateLicenseInput): Promise<CreatedLicense> {
  const res = await apiClient.post<{ data: CreatedLicense }>('/api/v1/admin/licenses', input);
  return res.data;
}

export async function revokeLicense(licenseId: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/licenses/${encodeURIComponent(licenseId)}`);
}

// ─── AI usage: /api/v1/admin/ai-usage ────────────────────────────────────────

export interface AdminAiUsageUser {
  userId: string;
  email: string | null;
  calls: number;
  errorCalls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  creditsUsed: number;
  /** Admin top-ups of the month (the welcome credit is separate). */
  topupCredits: number;
  /** Welcome credit included in the month's budget (optional for older APIs). */
  welcomeCredits?: number;
  /** welcomeCredits minus the month's usage. */
  welcomeRemaining?: number;
  /** ISO end of the welcome credit's window; null when none or expired. */
  welcomeExpiresAt?: string | null;
}

export interface AdminAiUsage {
  month: string;
  creditUsd: number;
  global: { spentUsd: number; capUsd: number | null; percent: number | null; reached: boolean };
  users: AdminAiUsageUser[];
}

export async function fetchAdminAiUsage(month: string): Promise<AdminAiUsage> {
  const res = await apiClient.get<{ data: AdminAiUsage }>(`/api/v1/admin/ai-usage?month=${encodeURIComponent(month)}`);
  return res.data;
}

export async function grantAiTopup(userId: string, credits: number, month: string): Promise<void> {
  await apiClient.post('/api/v1/admin/ai-usage/topups', { userId, credits, month });
}

// ─── Voice journal quality: /api/v1/admin/voice-quality ───────────────────────

/** Periods the voice quality report supports (days). */
export const VOICE_QUALITY_PERIODS = [7, 28, 90] as const;
export type VoiceQualityPeriod = (typeof VOICE_QUALITY_PERIODS)[number];

export interface VoiceQualityTiming {
  medianSeconds: number | null;
  p90Seconds: number | null;
  count: number;
}

export interface VoiceQualitySuggestionKind {
  source: 'voice' | 'typed';
  kind: string;
  notes: number;
  total: number;
  suggested: number;
  accepted: number;
  dismissed: number;
  expired: number;
  acceptedUnedited: number;
  edited: number;
  lowConfidence: number;
  acceptRate: number | null;
  acceptedWithoutEditRate: number | null;
  editRate: number | null;
  dismissRate: number | null;
  lowConfidenceShare: number | null;
  suppressed: boolean;
}

export interface VoiceQualityKpi {
  id: string;
  value: number | null;
  target: number;
  comparator: 'gte' | 'lte' | 'lt';
  unit: 'ratio' | 'seconds' | 'count' | 'usd';
  met: boolean | null;
}

/**
 * GET /api/v1/admin/voice-quality (src/services/adminVoiceQuality.service.ts):
 * aggregated voice journal KPIs — counts, rates and durations only; rates are
 * null for buckets with fewer than `minBucketSize` notes.
 */
export interface AdminVoiceQuality {
  period: { days: VoiceQualityPeriod; from: string; to: string };
  minBucketSize: number;
  retention: { days: number; complete: boolean };
  captures: {
    total: number;
    saved: number;
    reviewWaiting: number;
    discarded: number;
    inProgress: number;
    failed: number;
    failedByErrorCode: Array<{ code: string; count: number }>;
    savedShare: number | null;
    textOnly: number;
    textOnlyShare: number | null;
    uploadToProcessed: VoiceQualityTiming;
    processedToApplied: VoiceQualityTiming;
    uploadToApplied: VoiceQualityTiming;
    medianDurationSeconds: number | null;
    byLanguage: Array<{ language: string; count: number }>;
    suppressed: boolean;
  };
  suggestions: VoiceQualitySuggestionKind[];
  reviewReasons: Array<{ source: 'voice' | 'typed'; reason: string; count: number; accepted: number; dismissed: number }>;
  thumbs: { up: number; down: number; upShare: number | null; suppressed: boolean };
  reanalyses: { total: number; perNote: number | null };
  cost: {
    notesMetered: number;
    totalUsd: number;
    usdPerNote: number | null;
    creditsCharged: number;
    creditsPerNote: number | null;
    targetUsdPerNote: number;
    guidedSessions: { sessions: number; costUsd: number; creditsCharged: number };
    typedTaskDetection: { calls: number; costUsd: number; creditsCharged: number };
  };
  users: {
    weekly: { days: number; voiceUsers: number; activeUsers: number; share: number | null; suppressed: boolean };
    period: { voiceUsers: number; activeUsers: number };
  };
  kpis: VoiceQualityKpi[];
}

export async function fetchAdminVoiceQuality(days: VoiceQualityPeriod): Promise<AdminVoiceQuality> {
  const res = await apiClient.get<{ data: AdminVoiceQuality }>(`/api/v1/admin/voice-quality?days=${days}`);
  return res.data;
}

// ─── GDPR: /api/v1/admin/gdpr ────────────────────────────────────────────────

export interface InactiveUser {
  id: string;
  email: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
}

export async function fetchInactiveUsers(thresholdDays: number): Promise<{ users: InactiveUser[]; total: number }> {
  const res = await apiClient.get<{ data: { users: InactiveUser[]; total: number } }>(
    `/api/v1/admin/gdpr/inactive-users?thresholdDays=${encodeURIComponent(String(thresholdDays))}`,
  );
  return res.data;
}

/** Art. 15 export of one user's data (GET …/export → { data }) as a JSON string. */
export async function exportUserData(userId: string): Promise<string> {
  const res = await apiClient.get<{ data: unknown }>(`/api/v1/admin/gdpr/users/${encodeURIComponent(userId)}/export`);
  return JSON.stringify(res.data, null, 2);
}

export async function anonymizeUser(userId: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/gdpr/users/${encodeURIComponent(userId)}/anonymize`);
}

/** Current month as 'YYYY-MM' (UTC, like the AI budget month). */
export function currentMonth(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7);
}

